import { InfinityMark } from "./infinity-loader";
import {
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

/** Base visual de campos: plana, borda discreta e anel de foco suave. */
const FIELD =
  "w-full rounded-md border border-border-strong bg-bg text-[13px] text-fg placeholder:text-fg-subtle transition-[border-color,box-shadow] duration-[120ms] hover:border-[color-mix(in_oklab,var(--fg)_22%,var(--border-strong))] focus:border-ring focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_22%,transparent)] focus:outline-none disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(FIELD, "h-8 px-2.5", className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn(FIELD, "px-2.5 py-2", className)} {...props} />;
  },
);

export { Select, type SelectProps } from "./select";

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("mb-1.5 block text-xs font-medium text-fg-muted", className)} {...props} />;
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p role="alert" className="text-xs font-medium text-fg">
          ✕ {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export function Badge({
  className,
  variant = "outline",
  ...props
}: HTMLAttributes<HTMLSpanElement> & { variant?: "outline" | "solid" | "muted" }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-[5px] px-1.5 text-[11px] font-medium",
        variant === "solid" && "bg-fg text-fg-inverse",
        variant === "outline" && "border border-border-strong text-fg-muted",
        variant === "muted" && "bg-bg-emphasis text-fg-muted",
        className,
      )}
      {...props}
    />
  );
}

export function Kbd({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <kbd
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border border-border bg-bg-muted px-1 font-sans text-[11px] text-fg-muted",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Carregando (botões, listas, painéis): o símbolo do Kompil se formando. Mantém o tamanho pedido
 * pela classe (h-4 w-4 etc.): o laço ocupa a largura e fica centrado na altura.
 */
export function Spinner({ className }: { className?: string }) {
  return <InfinityMark className={cn("h-4 w-4", className)} stroke={11} track={false} />;
}

export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return (
    <div role="separator" className={cn(vertical ? "h-4 w-px" : "h-px w-full", "bg-border", className)} />
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("surface rounded-xl", className)} {...props} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton-shimmer rounded-lg", className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border px-6 py-12 text-center",
        className,
      )}
    >
      {icon ? (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-bg-subtle text-fg-muted">
          {icon}
        </div>
      ) : null}
      <div>
        <p className="font-medium text-fg">{title}</p>
        {description ? <p className="mt-1 max-w-sm text-[13px] text-fg-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/** Ponto de status monocromático: cheio = ativo, vazado = inativo, pulsando = em andamento. */
export function StatusDot({ state, className }: { state: "on" | "off" | "busy"; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block h-2 w-2 shrink-0 rounded-full",
        state === "on" && "bg-success",
        state === "off" && "border border-fg-subtle",
        state === "busy" && "animate-pulse-dot bg-fg-muted",
        className,
      )}
    />
  );
}

/** Move o holofote da classe `.spotlight` para a posição do mouse (variáveis --mx/--my). */
export function trackSpotlight(e: MouseEvent<HTMLElement>) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
}
